import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DropdownsService } from 'src/app/services/dropdowns/dropdowns.service';
import { Dropdown } from 'src/app/models/dropdown.model';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { DeviceInventoryService } from 'src/app/services/device-inventory/device-inventory.service';
import { MatDialog } from '@angular/material/dialog';
import { CustomerDetailsDialogComponent } from './customer-details-dialog/customer-details-dialog.component';
import { AssociateConfirmationDialogComponent } from './associate-confirmation-dialog/associate-confirmation-dialog.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-device-inventory',
  templateUrl: './device-inventory.component.html',
  styleUrls: ['./device-inventory.component.scss']
})
export class DeviceInventoryComponent implements OnInit {

  public faCalendarAlt = faCalendarAlt;
  
  public isSubmitted = false;
  public deviceGroups: Dropdown[];
  public deviceTypes: Dropdown[];
  public sensorProfiles: Dropdown[];
  public statuses: Dropdown[];

  constructor(
    private _fb: FormBuilder, 
    private _devicesService: DeviceInventoryService, 
    private _dropdownService: DropdownsService, 
    private _toastr: ToastrService,
    private _dialog: MatDialog,
        private _router: Router,
    
  ) { }

  public formDevice: FormGroup;

  ngOnInit() {
    this._createForm();
    this._getDeviceGroups();
    this._getDeviceTypes();
    this._getSensorProfiles();
    this._getStatuses();
  }

  private _createForm(){
    this.formDevice = this._fb.group({
      deviceId: [null, [Validators.required]],
      deviceGroup: null,
      deviceType: null,
      sensorProfile: null,
      hwRevision: null,
      fwRevision: null,
      startOfService: [new Date()],
      endOfLifecycle: null,
      endOfLicense: null,
      statusId: null,
    });
  }

  private _getDeviceGroups() {
    this._dropdownService.getDeviceGroups().subscribe(res => {
      this.deviceGroups = res;
      this.formDevice.controls['deviceGroup'].setValue(this.deviceGroups[0].code);
    });
  }

  private _getDeviceTypes() {
    this._dropdownService.getDeviceTypes().subscribe(res => {
      let filteredTypes = res.filter(item => item.code == 3 || item.code == 5).map(item => {
        let displayDescription = item.code == 3 ? 'Gateway' : 'ThingSendNode';
        return {
          ...item,
          displayDescription: displayDescription
        }
      });
    
      this.deviceTypes = filteredTypes;
      this.formDevice.controls['deviceType'].setValue(this.deviceTypes[0].code);
    });
  }

  private _getSensorProfiles() {
    this._dropdownService.getSensorProfiles().subscribe(res => {
      let filteredProfiles = res.filter(item => item.code == 2 || item.code == 4 || item.code == 5).map(item => {
        let displayDescription = item.code == 2 ? 'SensorTemp (Temp)' : item.code == 4 ? 'SensorTHM (Temp, Humidity)' : 
        'SensorTHCO (Temp, Humidity, CO2)';
        return {
          ...item,
          displayDescription: displayDescription
        }
      });
      this.sensorProfiles = filteredProfiles;
      this.formDevice.controls['sensorProfile'].setValue(this.sensorProfiles[0].code);
    });
  }

  private _getStatuses() {
    this._dropdownService.getStatuses().subscribe(res => {
      this.statuses = res;
      this.formDevice.controls['statusId'].setValue(this.statuses[0].code);
    });
  }

  onSubmit(){
    this.isSubmitted = true;
    if(this.formDevice.invalid){return}
    
    this.saveDeviceAndOpenCustomerDetails();
  }
  saveDeviceAndOpenCustomerDetails() {
    let data = this.formDevice.value;
    let defaultStatus = this.statuses.find(status => status.description === 'ACTIVE');
    data = {...data, statusId: defaultStatus.code}    
    
    this._devicesService.save(data).subscribe(res => {
      this._toastr.success('Device saved successfully.', '',{ timeOut: 3000 }); 
      this.onReset();
      this.openCustomerDetailsDialog(data.deviceId);
    }, error => {
      this._toastr.error('Error saving device.', 'Error');
    });
  }
  openAssociateConfirmationDialog() {
    const dialogRef = this._dialog.open(AssociateConfirmationDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'default') {
        this.saveDeviceWithDefaultCustomer();
      } else if (result === 'later') {
        this.saveDeviceAndOpenCustomerDetails();
      }
    });
  }

  saveDeviceWithDefaultCustomer() {
    let data = this.formDevice.value;
    let defaultStatus = this.statuses.find(status => status.description === 'ACTIVE');
    data = {...data, statusId: defaultStatus.code, env : 'dev'}    
    
    this._devicesService.save(data).subscribe(res => {
      this._toastr.success('Device saved successfully.', '',{ timeOut: 3000 }); 
      this.onReset();
      
      // Use hardcoded default values for cold store association
      const defaultColdStoreData = {
        customerId: 1, // Default customer ID
        userId: 1, // Default user ID
        siteId: 1, // Default site ID
        profileId: 1, // Default profile ID
        profileTypeId: 1, // Default profile type ID
        commodityId: 1, // Default commodity ID
        name: `Cold Store - ${data.deviceId}`, // Use device ID as the cold store name with hyphen separator
        latitude: 40.7128, // Default latitude (New York)
        longitude: -74.0060, // Default longitude (New York)
        companyName: 'Default Company' // Default company name
      };
      
      this._devicesService.saveColdStore({
        ...defaultColdStoreData, 
        deviceId: data.deviceId, 
        coordinates: defaultColdStoreData.latitude + ',' + defaultColdStoreData.longitude,
        environment: 'qa' // Default environment for default customer
      }, 'qa').subscribe(res => {
        this._toastr.success('Device associated with default customer agnext successfully.', '', { timeOut: 3000 });
      }, error => {
        this._toastr.error('Error associating device with default customer agnext.', 'Error');
      });
    }, error => {
      this._toastr.error('Error saving device.', 'Error');
    });
  }

  openCustomerDetailsDialog(deviceId: string) {
    const dialogRef = this._dialog.open(CustomerDetailsDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { showEnvironmentSelection: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._devicesService.saveColdStore({
          ...result, 
          deviceId: deviceId, 
          coordinates: result.latitude + ',' + result.longitude
        }, result.environment).subscribe(res => {
          this._toastr.success('Cold Store details saved successfully.', '', { timeOut: 3000 });
        }, error => {
          this._toastr.error('Error saving cold store details.', 'Error');
        });
      } else {
        this._toastr.info('Device saved successfully. You can onboard coldstore details later from the device list.', 'Onboard Later', { timeOut: 5000 });
      }
    });
  }
  
  onReset() {
    // this.openCustomerDetailsDialog();
    this.isSubmitted = false;
    this.formDevice.reset();
    this.formDevice.markAsPristine();
    this.formDevice.controls['deviceGroup'].setValue(this.deviceGroups[0].code);
    this.formDevice.controls['deviceType'].setValue(this.deviceTypes[0].code);
    this.formDevice.controls['sensorProfile'].setValue(this.sensorProfiles[0].code);
  }
  
  get f(){ return this.formDevice.controls }

  // Date filter to disable dates before today
  dateFilter = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }

  goToDeviceList() {
    this._router.navigate(["/landing/common-inventory/all"]);
  }
} 




